import {
  IFIReportType,
  LotCompactionLevel,
  notEqual,
} from '@cims-online/shared'
import { CommonEmbeddedEditPanelProps } from '../../../common'
import { DensityLotPanelName } from '../../types'
import { useEffect, useState } from 'react'
import { DensityLotFormValues } from './types'
import Form, { FormProps, useForm } from 'antd/lib/form/Form'
import CIMSPrompt from 'components/CIMSPrompt'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import { useUpsertLotIfiSettingsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { ifiSettingsEditNotification, momentNotEqual } from '../../../../utils'
import DensityLotEditPanelCollapse from './components/DensityLotEditPanelCollapse'
import { toMoment } from 'utils/toMoment'
import EditPanelFooter from '../../../../components/EditPanelFooter'
import ImplicitEnablePanelHeader from '../../../../components/ImplicitEnablePanelHeader'
import { useProjectContext } from 'context/ProjectContext'

const DensityLotEditPanel: React.VFC<
  CommonEmbeddedEditPanelProps<DensityLotPanelName>
> = ({ dirtyPanels, setDirtyPanels, ...collapseProps }) => {
  const { projectId } = useProjectContext()
  const {
    lotIFISettings: { data, refetch },
  } = useProjectSettingsContext()
  const [upsert, { loading }] = useUpsertLotIfiSettingsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => ifiSettingsEditNotification(IFIReportType.Lot.type),
  })
  const [initialValues, setInitialValues] = useState<DensityLotFormValues>({})
  const [form] = useForm<DensityLotFormValues>()

  const handleOnValuesChange: FormProps<DensityLotFormValues>['onValuesChange'] =
    (_, currentValues) => {
      const newDirtyPanels: DensityLotPanelName[] = []
      if (
        momentNotEqual(initialValues.effectiveDate, currentValues.effectiveDate)
      )
        newDirtyPanels.push('parent')
      if (
        notEqual(
          initialValues.testCountEnabled,
          currentValues.testCountEnabled
        ) ||
        notEqual(initialValues.testCountMessage, currentValues.testCountMessage)
      )
        newDirtyPanels.push('count')
      if (
        notEqual(initialValues.proctorEnabled, currentValues.proctorEnabled) ||
        notEqual(initialValues.proctorMessage, currentValues.proctorMessage)
      )
        newDirtyPanels.push('proctor')
      if (
        notEqual(
          initialValues.compactionEnabled,
          currentValues.compactionEnabled
        ) ||
        notEqual(
          initialValues.compactionLevelId,
          currentValues.compactionLevelId
        ) ||
        notEqual(
          initialValues.compactionMessage,
          currentValues.compactionMessage
        )
      )
        newDirtyPanels.push('compaction')

      setDirtyPanels(newDirtyPanels)
    }

  useEffect(() => {
    form.setFieldsValue(initialValues)
    handleOnValuesChange({}, form.getFieldsValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    setInitialValues({
      effectiveDate: toMoment(data?.effectiveDate),
      testCountEnabled: data?.testCount?.enabled,
      testCountMessage: data?.testCount?.message,
      proctorEnabled: data?.proctor?.enabled,
      proctorMessage: data?.proctor?.message,
      compactionEnabled: data?.compaction?.enabled,
      compactionLevelId: data?.compaction?.levelId ?? LotCompactionLevel.Lot.id,
      compactionMessage: data?.compaction?.message,
    })
  }, [data])

  return (
    <CIMSPrompt when={dirtyPanels.length > 0}>
      <Form
        form={form}
        onValuesChange={handleOnValuesChange}
        initialValues={initialValues}
        onFinish={async ({
          effectiveDate,
          testCountEnabled,
          testCountMessage,
          proctorEnabled,
          proctorMessage,
          compactionEnabled,
          compactionLevelId,
          compactionMessage,
        }) => {
          await upsert({
            variables: {
              input: {
                projectId,
                effectiveDate:
                  effectiveDate === null ? null : effectiveDate?.toISOString(),
                testCount: {
                  enabled: testCountEnabled,
                  message: testCountMessage,
                },
                proctor: {
                  enabled: proctorEnabled,
                  message: proctorMessage,
                },
                compaction: {
                  enabled: compactionEnabled,
                  levelId: compactionLevelId,
                  message: compactionMessage,
                },
              },
            },
          })
          refetch()
        }}
      >
        <ImplicitEnablePanelHeader
          reportType={IFIReportType.Lot.type}
          effectiveDate={initialValues.effectiveDate}
        />
        <DensityLotEditPanelCollapse
          {...{ form, dirtyPanels, ...collapseProps }}
        />
        <EditPanelFooter
          {...{
            form,
            loading,
            handleOnValuesChange,
            hasChanges: dirtyPanels.length > 0,
          }}
        />
      </Form>
    </CIMSPrompt>
  )
}

export default DensityLotEditPanel
